import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardHeader, Chip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { getUserLocationsData } from '../../services/reports.service';
import LocationStatsPanel from './location-stats-panel.component';
import HeatmapMapView from './heatmap-map-view.component';
import Loading from '../table-elements/loading.component';

const DEFAULT_CENTER = [0, 20];

const normalizeCoordinates = (coordinates, fallback = null) => {
  if (!Array.isArray(coordinates) || coordinates.length !== 2) return fallback;

  const normalized = coordinates.map((coord) => Number(coord));

  if (normalized.some((coord) => Number.isNaN(coord))) {
    return fallback;
  }

  return normalized;
};

const normalizePlatforms = (platforms = []) =>
  (platforms || [])
    .map((platform) => ({
      label: String(platform.label || '').trim(),
      value: Number(platform.value) || 0,
    }))
    .filter((platform) => platform.label)
    .sort((a, b) => b.value - a.value);

const buildFallbackCoordinates = (cities = [], fallback = DEFAULT_CENTER) => {
  const validCoordinates = cities
    .map((city) => normalizeCoordinates(city.coordinates))
    .filter(Boolean);

  if (!validCoordinates.length) return fallback;

  const [longitudeSum, latitudeSum] = validCoordinates.reduce(
    (acc, [longitude, latitude]) => [acc[0] + longitude, acc[1] + latitude],
    [0, 0]
  );

  return [
    Number((longitudeSum / validCoordinates.length).toFixed(4)),
    Number((latitudeSum / validCoordinates.length).toFixed(4)),
  ];
};

const normalizeLocationsResponse = (payload) => {
  const countries = (payload?.countries || [])
    .map((country, countryIndex) => {
      const countryId = String(country.id || country.code || '')
        .trim()
        .toUpperCase();
      const countryName = String(
        country.country || `Country ${countryIndex + 1}`
      ).trim();

      const cities = (country.cities || []).map((city, cityIndex) => {
        const cityName = String(city.name || `City ${cityIndex + 1}`).trim();

        return {
          ...city,
          id: `${countryId || countryName}-${cityName}-${cityIndex}`,
          name: cityName,
          country: countryName,
          regionId: countryId || `COUNTRY-${countryIndex + 1}`,
          coordinates: normalizeCoordinates(city.coordinates),
          uniqueDevices: Number(city.uniqueDevices ?? city.value) || 0,
          value: Number(city.uniqueDevices ?? city.value) || 0,
          platforms: normalizePlatforms(city.platforms),
        };
      });

      const coordinates =
        normalizeCoordinates(country.coordinates) ||
        buildFallbackCoordinates(cities, [0, 20]);

      return {
        ...country,
        id: countryId || `COUNTRY-${countryIndex + 1}`,
        country: countryName,
        name: countryName,
        coordinates,
        uniqueDevices: Number(country.uniqueDevices ?? country.value) || 0,
        value: Number(country.uniqueDevices ?? country.value) || 0,
        platforms: normalizePlatforms(country.platforms),
        cities: cities.map((city) => ({
          ...city,
          coordinates: city.coordinates || coordinates,
        })),
      };
    })
    .filter((country) => country.name);

  const globalPlatforms = normalizePlatforms(payload?.global?.platforms);
  const globalUniqueDevices =
    Number(payload?.global?.uniqueDevices) ||
    countries.reduce((sum, country) => sum + (country.uniqueDevices || 0), 0);

  const allPlatformLabels = Array.from(
    new Set([
      ...globalPlatforms.map((platform) => platform.label),
      ...countries.flatMap((country) => [
        ...country.platforms.map((platform) => platform.label),
        ...country.cities.flatMap((city) =>
          (city.platforms || []).map((platform) => platform.label)
        ),
      ]),
    ])
  ).sort();

  return {
    global: {
      uniqueDevices: globalUniqueDevices,
      platforms: globalPlatforms,
    },
    countries,
    platformLabels: allPlatformLabels,
  };
};

const UserLocationsHeatmap = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [locationsData, setLocationsData] = useState(null);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [selectedCountryFocusCoordinates, setSelectedCountryFocusCoordinates] =
    useState(null);
  const [highlightedDotId, setHighlightedDotId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [breakdownMode, setBreakdownMode] = useState('platform');
  const [activePlatforms, setActivePlatforms] = useState([]);

  const selectedCountry = useMemo(() => {
    if (!selectedCountryId || !locationsData?.countries?.length) return null;

    return (
      locationsData.countries.find(
        (country) => country.id === selectedCountryId
      ) || null
    );
  }, [locationsData, selectedCountryId]);

  const handleCountrySelect = useCallback((countryPayload) => {
    if (!countryPayload?.id) return;

    setSelectedCountryId(countryPayload.id);
    setSelectedCountryFocusCoordinates(countryPayload.focusCoordinates || null);
    setHighlightedDotId(null);
  }, []);

  const handleReset = useCallback(() => {
    setSelectedCountryId(null);
    setSelectedCountryFocusCoordinates(null);
    setHighlightedDotId(null);
  }, []);

  const handleCitySelect = useCallback(
    (cityPayload) => {
      if (!cityPayload?.id) return;

      if (cityPayload.regionId && cityPayload.regionId !== selectedCountryId) {
        const nextCountry = locationsData?.countries?.find(
          (country) => country.id === cityPayload.regionId
        );

        if (nextCountry) {
          setSelectedCountryId(nextCountry.id);
          setSelectedCountryFocusCoordinates(
            nextCountry.focusCoordinates || null
          );
        }
      }

      setHighlightedDotId(cityPayload.id);
    },
    [locationsData, selectedCountryId]
  );

  const handleTogglePlatform = useCallback((platformLabel) => {
    setActivePlatforms((previous) => {
      const isActive = previous.includes(platformLabel);

      if (isActive) {
        return previous.length > 1
          ? previous.filter((item) => item !== platformLabel)
          : previous;
      }

      return [...previous, platformLabel];
    });
  }, []);

  const handleBreakdownModeChange = useCallback(
    (nextMode) => {
      if (nextMode === breakdownMode) return;

      setBreakdownMode(nextMode);
      handleReset();
    },
    [breakdownMode]
  );

  useEffect(() => {
    setLoading(true);
    setError(false);

    getUserLocationsData(breakdownMode)
      .then((res) => {
        const normalizedData = normalizeLocationsResponse(res.data);
        setLocationsData(normalizedData);
        setActivePlatforms(normalizedData.platformLabels);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [breakdownMode]);

  return (
    <Card>
      <CardHeader
        title="User Locations (Last 30 Days)"
        subheader="User locations based on app visits in the last 30 days"
        action={
          <Chip
            label="Heatmap"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: theme.palette.primary.light,
            }}
          />
        }
      />
      {loading && <Loading />}

      {!loading && error && (
        <Typography
          variant="body2"
          sx={{
            my: 20,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          Something went wrong. Please try again.
        </Typography>
      )}

      {!loading && !error && locationsData && (
        <Box sx={{ p: 3 }}>
          <Box sx={{ position: 'relative', overflow: 'hidden' }}>
            <HeatmapMapView
              locationsData={locationsData}
              breakdownMode={breakdownMode}
              selectedCountry={selectedCountry}
              selectedCountryFocusCoordinates={selectedCountryFocusCoordinates}
              highlightedDotId={highlightedDotId}
              activePlatforms={activePlatforms}
              onCountrySelect={handleCountrySelect}
              onCitySelect={handleCitySelect}
              onReset={handleReset}
            />

            <LocationStatsPanel
              locationsData={locationsData}
              breakdownMode={breakdownMode}
              selectedCountry={selectedCountry}
              highlightedDotId={highlightedDotId}
              isOpen={sidebarOpen}
              activePlatforms={activePlatforms}
              onCountrySelect={handleCountrySelect}
              onCitySelect={handleCitySelect}
              onReset={handleReset}
              onToggle={() => setSidebarOpen((previous) => !previous)}
              onBreakdownModeChange={handleBreakdownModeChange}
              onTogglePlatform={handleTogglePlatform}
            />
          </Box>
        </Box>
      )}
    </Card>
  );
};

export default UserLocationsHeatmap;
